import { CuriosityAutocomplete, CuriosityNamespace, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TsrctDemandFilterField,
  TsrctDemandProcessStatus,
  TsrctDemandProcessingMode,
  TsrctDemandStatus,
  TsrctDemandProcessStepVariant,
  TsrctDemandProcessStepSentinel,
} from '@/generated/gql/graphql';
import { AstronautUserAutocomplete } from '~/components/AstronautUserAutocomplete';
import {
  listOperators,
  operators,
  queryFieldFactory,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';
import { SupplierAutocomplete } from '~/components/SupplierAutocomplete';
import {
  ValueEditorSelectAutocomplete,
  ValueEditorSelectAutocompleteProps,
} from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';

enum NegotiationDemandQueryFieldGroup {
  DEMAND = 'demand',
  REQUESTED = 'requested',
  RESPONSIBLE = 'responsible',
  PURCHASE_REQUISITION = 'purchaseRequisition',
  DELIVERY_DATE = 'deliveryDate',
  CASE = 'case',
}

const curiosityNsMap: { [key in TsrctDemandFilterField]?: CuriosityNamespace } = {
  [TsrctDemandFilterField.capability]: CuriosityNamespaceType.CAPABILITY,
  [TsrctDemandFilterField.commodityGroup]: CuriosityNamespaceType.COMMODITY_GROUP,
  [TsrctDemandFilterField.organization]: CuriosityNamespaceType.ORGANIZATION,
  [TsrctDemandFilterField.primaryCommodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [TsrctDemandFilterField.requestedBy]: CuriosityNamespaceType.INTERNAL_USER,
  [TsrctDemandFilterField.sourceSystemId]: CuriosityNamespaceType.SOURCE_SYSTEM,
};

const enumAutocompleteMap: {
  [key in TsrctDemandFilterField]?: Pick<ValueEditorSelectAutocompleteProps, 'initialI18nPrefix' | 'initialOptions'>;
} = {
  [TsrctDemandFilterField.priority]: {
    initialI18nPrefix: 'DemandPriority',
    initialOptions: [0, 1, 2, 3],
  },
  [TsrctDemandFilterField.processingMode]: {
    initialI18nPrefix: 'DemandProcessingMode',
    initialOptions: Object.keys(TsrctDemandProcessingMode),
  },
  [TsrctDemandFilterField.processStatus]: {
    initialI18nPrefix: 'DemandProcessStatus',
    initialOptions: Object.keys(TsrctDemandProcessStatus),
  },
  [TsrctDemandFilterField.processSteps]: {
    initialI18nPrefix: 'DemandProcessStepVariant',
    initialOptions: [...Object.keys(TsrctDemandProcessStepVariant), ...Object.keys(TsrctDemandProcessStepSentinel)],
  },
};

export const useNegotiationDemandDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'DemandQueryBuilder' });
    return {
      fields: [
        // ---------- DEMAND ----------
        createField({
          name: TsrctDemandFilterField.sequenceNumber,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.numNotEmpty,
        }),
        createField({
          name: TsrctDemandFilterField.priority,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: TsrctDemandFilterField.primaryCommodityGroupId,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctDemandFilterField.commodityGroup,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctDemandFilterField.processingMode,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: TsrctDemandFilterField.processSteps,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: TsrctDemandFilterField.totalValue,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.num,
        }),
        createField({
          name: TsrctDemandFilterField.preferredSupplier,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctDemandFilterField.status,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.enumValue,
        }),
        createField({
          name: TsrctDemandFilterField.processStatus,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: TsrctDemandFilterField.requestedAt,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.date,
        }),
        createField({
          name: TsrctDemandFilterField.updatedAt,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.date,
        }),
        createField({
          name: TsrctDemandFilterField.createdAt,
          grp: NegotiationDemandQueryFieldGroup.DEMAND,
          operators: operators.date,
        }),
        // ---------- REQUESTED ----------
        createField({
          name: TsrctDemandFilterField.organization,
          grp: NegotiationDemandQueryFieldGroup.REQUESTED,
          operators: operators.idAuto,
        }),
        // ---------- DELIVERY DATE ----------
        createField({
          name: TsrctDemandFilterField.earlyDeliveryAt,
          grp: NegotiationDemandQueryFieldGroup.DELIVERY_DATE,
          operators: operators.date,
        }),
        createField({
          name: TsrctDemandFilterField.lateDeliveryAt,
          grp: NegotiationDemandQueryFieldGroup.DELIVERY_DATE,
          operators: operators.date,
        }),
        // ---------- RESPONSIBLE ----------
        createField({
          name: TsrctDemandFilterField.responsibleIds,
          grp: NegotiationDemandQueryFieldGroup.RESPONSIBLE,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctDemandFilterField.capability,
          grp: NegotiationDemandQueryFieldGroup.RESPONSIBLE,
          operators: operators.idAuto,
        }),
        // ---------- PURCHASE_REQUISITION ----------
        createField({
          name: TsrctDemandFilterField.referenceId,
          grp: NegotiationDemandQueryFieldGroup.PURCHASE_REQUISITION,
          operators: operators.text,
        }),
        createField({
          name: TsrctDemandFilterField.requestedBy,
          grp: NegotiationDemandQueryFieldGroup.PURCHASE_REQUISITION,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctDemandFilterField.sourceSystemId,
          grp: NegotiationDemandQueryFieldGroup.PURCHASE_REQUISITION,
          operators: operators.idAuto,
        }),
        // ---------- CASE ----------
        createField({
          name: TsrctDemandFilterField.casesTotalCount,
          grp: NegotiationDemandQueryFieldGroup.CASE,
          operators: operators.numNotEmpty,
        }),
        createField({
          name: TsrctDemandFilterField.casesNegotiatedCount,
          grp: NegotiationDemandQueryFieldGroup.CASE,
          operators: operators.numNotEmpty,
        }),
        createField({
          name: TsrctDemandFilterField.casesOpenCount,
          grp: NegotiationDemandQueryFieldGroup.CASE,
          operators: operators.numNotEmpty,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case TsrctDemandFilterField.lateDeliveryAt:
          case TsrctDemandFilterField.earlyDeliveryAt:
          case TsrctDemandFilterField.requestedAt:
          case TsrctDemandFilterField.createdAt:
          case TsrctDemandFilterField.updatedAt:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          //
          case TsrctDemandFilterField.referenceId:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          case TsrctDemandFilterField.priority:
          case TsrctDemandFilterField.processStatus:
          case TsrctDemandFilterField.processSteps:
          case TsrctDemandFilterField.processingMode: {
            const enumOptions = enumAutocompleteMap[field];
            if (enumOptions) {
              return (
                <ValueEditorSelectAutocomplete {...otherProps} {...enumOptions} multiple={listOperators[operator]} />
              );
            }
            return null;
          }
          case TsrctDemandFilterField.status:
            return (
              <ValueEditorSelectAutocomplete
                {...otherProps}
                initialI18nPrefix="DemandStatus"
                initialOptions={Object.keys(TsrctDemandStatus)}
                multiple={listOperators[operator]}
              />
            );
          case TsrctDemandFilterField.organization:
          case TsrctDemandFilterField.primaryCommodityGroupId:
          case TsrctDemandFilterField.commodityGroup:
          case TsrctDemandFilterField.requestedBy:
          case TsrctDemandFilterField.sourceSystemId:
          case TsrctDemandFilterField.capability: {
            const ns = curiosityNsMap[field];
            if (ns) {
              return <CuriosityAutocomplete ns={ns} multiple={listOperators[operator]} simplifyValue {...otherProps} />;
            }
            return null;
          }
          case TsrctDemandFilterField.responsibleIds:
            return (
              <AstronautUserAutocomplete
                multiple={listOperators[operator]}
                hasEmptyOption
                translationPrefix="ResponsibleUsersSelect.options"
                {...otherProps}
              />
            );
          case TsrctDemandFilterField.preferredSupplier:
            return <SupplierAutocomplete multiple={listOperators[operator]} simplifyValue {...otherProps} />;
          case TsrctDemandFilterField.sequenceNumber:
          case TsrctDemandFilterField.casesTotalCount:
          case TsrctDemandFilterField.casesNegotiatedCount:
          case TsrctDemandFilterField.casesOpenCount:
          case TsrctDemandFilterField.totalValue:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          case TsrctDemandFilterField.requestedAt:
          case TsrctDemandFilterField.updatedAt:
          case TsrctDemandFilterField.createdAt:
          case TsrctDemandFilterField.earlyDeliveryAt:
          case TsrctDemandFilterField.lateDeliveryAt:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
